import { RequestCache } from './request-cache';
import { resolveURL } from '@utilities/url-resolver';
import { DEFAULT_EXPIRATION_TIME } from './constants';
import BackendResponse from '@core/shared/backend-response';
import AttachTokenInterceptor from './interceptors/token.interceptor';
import axios, { AxiosRequestConfig, AxiosInstance, AxiosResponse } from 'axios';
import sessionExpirationCheckInterceptor from './interceptors/session-expiration.interceptor';

/**
 * ### Axios Request Adapter Module
 *
 * Adapter for axios instance:
 *
 * - resolve urls
 * - add auth token
 * - cache API for get requests
 * - without token calls
 *
 * @author <pino0071@gmail.com> Jose Aburto
 * @version v1
 */
export class AxiosRequestAdapter extends RequestCache {
  public readonly instance: AxiosInstance = sessionExpirationCheckInterceptor(AttachTokenInterceptor(axios.create()));

  public async rawGet<T>(url: string, configs: AxiosRequestConfig<any>): Promise<AxiosResponse<T>> {
    return await this.instance.get<T>(resolveURL(url), configs);
  }

  public get<T>(url: string, configs: AxiosRequestConfig<any>): Promise<AxiosResponse<BackendResponse<T>>> {
    return new Promise(async (resolve, reject) => {
      if (this.isFirstCall(url)) {
        try {
          const response = await this.getRequest<T>(url, configs);
          this.urlExpirationTimes[url] = DEFAULT_EXPIRATION_TIME;
          return resolve(response);
        } catch (error) {
          return reject(error);
        }
      }

      if (this.isURLExpired(url)) {
        try {
          const response = await this.getRequest<T>(url, configs);
          return resolve(response);
        } catch (error) {
          return reject(error);
        }
      }

      if (url in this.cachedCalls) {
        const response = this.getCachedCalls(url) as AxiosResponse<BackendResponse<T>>;
        return resolve(response);
      }

      try {
        const response = await this.getRequest<T>(url, configs);
        return resolve(response);
      } catch (error) {
        return reject(error);
      }
    });
  }

  public delete<T>(url: string, configs: AxiosRequestConfig<any>) {
    return this.instance.delete<BackendResponse<T>>(resolveURL(url), configs);
  }

  public post<T, R>(url: string, data: T, configs: AxiosRequestConfig<any>) {
    return this.instance.post<BackendResponse<R>>(resolveURL(url), data, configs);
  }

  public put<T>(url: string, data: T, configs: AxiosRequestConfig<any>) {
    return this.instance.put<BackendResponse<T>>(resolveURL(url), data, configs);
  }

  public freePost<T, R>(url: string, data: T, configs: AxiosRequestConfig<any>) {
    return axios.post<BackendResponse<R>>(resolveURL(url), data, configs);
  }

  public freeGet<T>(url: string, configs: AxiosRequestConfig<any>) {
    return axios.get<BackendResponse<T>>(resolveURL(url), configs);
  }

  private async getRequest<T>(url: string, configs: AxiosRequestConfig<any>): Promise<AxiosResponse<BackendResponse<T>>> {
    const response = await this.instance.get<BackendResponse<T>>(resolveURL(url), configs);
    this.cachedCalls[url] = response;
    this.urlFirstCallTime[url] = Date.now();
    return response;
  }
}
